import { Download } from '@mui/icons-material';
import {
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { Button, Loader, Table } from '../../components';

import { downloadFile, getDownloadFileName } from '../../helpers';
import { LoadingButton } from '@mui/lab';
import { IDropdownResponse } from '../../models';

interface IBulkDownloadTable {
  data: IDropdownResponse[];
  downloadApiRequest?: (docs: any, filters?: any) => Promise<any>;
  downloadApiParams?: any;
  isLoading?: boolean;
  selectedDocs: IDropdownResponse[];
  setSelectedDocs: React.Dispatch<React.SetStateAction<IDropdownResponse[]>>;
  bundleLabel?: string;
  transformFile?: (label?: string) => Promise<any>; // For Style Guide Purposes
  bulkTransformFile?: (docs: any, label?: string) => Promise<any>; // For Style Guide Purposes
}

export const BulkDownloadTable: FC<IBulkDownloadTable> = ({
  data,
  downloadApiRequest,
  downloadApiParams,
  isLoading,
  selectedDocs,
  setSelectedDocs,
  bundleLabel,
  transformFile,
  bulkTransformFile
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [isAllSelected, setSelectedAll] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState<boolean>(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const isMobile = useMediaQuery(`(max-width: 900px)`);

  const downloadSampleContractDocs = async (docs, isBulk = false) => {
    try {
      if (downloadApiRequest) {
        const res = downloadApiParams
          ? await downloadApiRequest(docs, downloadApiParams)
          : await downloadApiRequest(docs);
        downloadFile(res.data, getDownloadFileName(res) || bundleLabel || 'documents.zip');
      } else if (bulkTransformFile || transformFile) {
        isBulk ? bulkTransformFile(docs, bundleLabel ?? 'documents.zip') : transformFile(docs);
      }
    } catch (error) {
      enqueueSnackbar(error.Detail || 'Error downloading, please refresh and try again.', {
        variant: 'error'
      });
      console.log(error);
    }
  };

  const handleDelete = chipToDelete => () => {
    setSelectedDocs(selectedDocs =>
      selectedDocs.filter(selectedDoc => selectedDoc.value !== chipToDelete.value)
    );
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: ' ',
        overrideWidth: 50,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDropdownResponse } };
        }) => {
          const tableDoc = original;
          const ids = selectedDocs.map(tableDoc => tableDoc);
          const isSelected = ids.includes(tableDoc);

          return (
            <FormControlLabel
              classes={{
                label: classes.tableCheckbox
              }}
              color="primary"
              control={
                <Checkbox
                  id="documents-checkbox"
                  checked={isSelected}
                  onChange={() => {
                    isSelected
                      ? setSelectedDocs(
                          selectedDocs.filter(selectedDoc => selectedDoc.value !== tableDoc.value)
                        )
                      : setSelectedDocs([...selectedDocs, tableDoc]);
                  }}
                />
              }
              label=""
            />
          );
        }
      },
      {
        Header: ' ',
        accessor: 'description',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: ' ',
        sort: false,
        overrideWidth: isMobile ? 48 : 180,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDropdownResponse } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id={`single_download_${original.value}`}
                color="primary"
                onClick={async () => {
                  setIsDownloading(true);
                  await downloadSampleContractDocs([original.value]);
                  setIsDownloading(false);
                }}
                size="medium"
                title="Download"
                disabled={isDownloading}
                startIcon={<Download />}
              >
                {isMobile ? '' : 'Download'}
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedDocs]);

  return (
    <div style={{ position: 'relative' }}>
      {((isLoading && data) || isDownloading) && (
        <Loader type="overlay" position="centered">
          {isDownloading ? '' : 'Loading...'}
        </Loader>
      )}
      {selectedDocs.length !== 0 && (
        <>
          <Grid container>
            <Grid item xs={12} sm={9}>
              <List className={classes.selectedFilesList}>
                <ListItem key={'files-selected'}>
                  <Typography>Files selected:</Typography>
                </ListItem>
                {selectedDocs.map(selectedDoc => {
                  return (
                    <ListItem key={selectedDoc.value}>
                      <Chip label={selectedDoc.shorthand} onDelete={handleDelete(selectedDoc)} />
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid item xs={12} sm={3} className={classes.bulkDownloadBtnWrap}>
              <LoadingButton
                id={'bulk_download'}
                color="primary"
                variant="contained"
                onClick={async () => {
                  setIsBulkDownloading(true);
                  await downloadSampleContractDocs(
                    selectedDocs.map(selectedDoc => {
                      return selectedDoc.value;
                    }),
                    true
                  );
                  setIsBulkDownloading(false);
                }}
                disabled={!data || isBulkDownloading || selectedDocs.length < 2}
                loading={isBulkDownloading}
                startIcon={<Download />}
              >
                Download Selection
              </LoadingButton>
            </Grid>
          </Grid>
          <Divider />
        </>
      )}
      <div className={classes.actionsWrapper}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isAllSelected}
              id="documents-checkbox"
              onChange={() => {
                if (isAllSelected) {
                  setSelectedDocs([]);
                } else {
                  setSelectedDocs(data);
                }
                setSelectedAll(!isAllSelected);
              }}
            />
          }
          label="Select All"
        />
        <LoadingButton
          id="download-all-contract-documents"
          type="submit"
          color="primary"
          variant="contained"
          disabled={!data || isDownloadingAll}
          loading={isDownloadingAll}
          startIcon={<Download />}
          onClick={async () => {
            setIsDownloadingAll(true);
            await downloadSampleContractDocs(
              data.map(selectedDoc => selectedDoc.value),
              true
            );
            setIsDownloadingAll(false);
          }}
        >
          Download All
        </LoadingButton>
      </div>
      <Divider />
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileBreakPoint={1400}
        mobileProps={() => {}}
        containerClasses={classes.tableContainer}
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    display: 'flex'
  },
  actionsWrapper: {
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tableContainer: {
    '& .MuiTableHead-root': {
      display: 'none'
    }
  },
  tableCheckbox: {
    marginRight: 0
  },
  selectedFilesList: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.5, 0),
    margin: 0,
    '& .MuiListItem-root': {
      width: 'auto',
      padding: theme.spacing(0.5, 0.25)
    }
  },
  bulkDownloadBtnWrap: {
    '& .MuiButton-root': {
      [theme.breakpoints.up('sm')]: {
        alignSelf: 'center'
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginBottom: theme.spacing(2)
      }
    },
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  }
}));
